// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import { isCompensationStepComplete, isBaseAmountStepComplete } from '../../../utils/compensationStep';
import { Briefcase, Calculator, ChevronRight, DollarSign, FileCheck, FileText, PieChart, Users } from 'lucide-react';
import { getInitialSidebarCollapsed } from '../../../utils/initialSidebarCollapsed';
import {
  DEFAULT_PARACHUTE_EQUITY_SUB_ID,
  DEFAULT_PARACHUTE_WORKFLOW_SUB_ID,
  PARACHUTE_WORKFLOW_SUB_ITEMS,
} from '../parachuteWorkflowSubItems';
import { DEFAULT_DOCUMENTS_WORKFLOW_SUB_ID, DOCUMENTS_WORKFLOW_SUB_ITEMS } from '../documentsWorkflowSubItems';

/** True when this Parachute Payments subsection has saved / in-progress data (same rules as sidebar “done”). */
export function isParachuteSubItemComplete(fd, subId) {
  switch (subId) {
    case 'severance':
      return Object.keys(fd.severanceData || {}).length > 0;
    case 'cicBonus': {
      const cic = fd.cicBonusData || {};
      return Object.keys(cic).some((k) => Number(cic[k]) > 0);
    }
    case 'compIncreases': {
      const compI = fd.compIncreaseData || {};
      return Object.values(compI).some((row) => row && typeof row === 'object' && row.hasIncrease === 'yes');
    }
    case 'equity': {
      const eq = fd.equityData || {};
      return Object.values(eq).some((row) => row && typeof row === 'object' && (row.awards || []).length > 0);
    }
    case 'buyerEquity': {
      const be = fd.buyerEquityData || {};
      return Object.values(be).some((row) => row && typeof row === 'object' && (row.awards || []).length > 0);
    }
    case 'otherPayments': {
      const op = fd.otherPaymentsData || {};
      return Object.values(op).some((row) => row && typeof row === 'object' && (row.payments || []).length > 0);
    }
    default:
      return false;
  }
}

export function hasParachuteProgress(fd) {
  return PARACHUTE_WORKFLOW_SUB_ITEMS.some((sub) => isParachuteSubItemComplete(fd, sub.id));
}

function buildWorkflowSteps(formData) {
  const individuals = formData.individuals || [];
  return [
    {
      id: 'transaction',
      label: 'Transaction',
      desc: 'Company, closing, deal price',
      done: !!formData.companyName,
      icon: FileText,
    },
    {
      id: 'individuals',
      label: 'Individuals',
      desc: 'HCIs, officers, shareholders',
      done: individuals.length > 0,
      icon: Users,
    },
    {
      id: 'compensation',
      label: 'Compensation',
      desc: 'Start date & base salary required',
      done: isCompensationStepComplete(formData),
      icon: DollarSign,
    },
    {
      id: 'baseAmount',
      label: 'Base Amount',
      desc: '5-year W-2 Box 1 history',
      done: isBaseAmountStepComplete(formData),
      icon: Calculator,
    },
    {
      id: 'parachute',
      label: 'Parachute Payments',
      desc: 'Severance, equity, bonuses',
      done: hasParachuteProgress(formData),
      icon: Briefcase,
    },
    { id: 'analysis', label: '280G Results', desc: 'Threshold test & results', done: false, icon: PieChart },
    { id: 'documents', label: 'Shareholder Approval', desc: 'Document generation', done: false, icon: FileCheck },
  ];
}

function useWorkflowSteps(formData) {
  // Depend on formData identity so nested edits (new object from setState) always refresh.
  return useMemo(() => buildWorkflowSteps(formData), [formData]);
}

/**
 * Left rail: workflow step list (expanded + collapsed), Parachute submenu.
 */
export function WorkflowStepsPanel({
  formData,
  selectedStepId,
  setSelectedStepId,
  parachuteSubId,
  setParachuteSubId,
  parachuteEquitySubId,
  setParachuteEquitySubId,
  parachuteExpanded,
  setParachuteExpanded,
  documentsSubId,
  setDocumentsSubId,
  documentsExpanded,
  setDocumentsExpanded,
  persistBeforeNavigate,
}) {
  const [stepsCollapsed, setStepsCollapsed] = useState(() => getInitialSidebarCollapsed());
  const steps = useWorkflowSteps(formData);

  const isParachuteActive = selectedStepId === 'parachute';
  const expanded = parachuteExpanded || isParachuteActive;
  const isDocumentsActive = selectedStepId === 'documents';
  const documentsRowExpanded = documentsExpanded || isDocumentsActive;

  const withPersist = useCallback(
    async (navigate) => {
      if (persistBeforeNavigate) {
        try {
          await persistBeforeNavigate();
        } catch (e) {
          console.error(e);
          alert('Could not save analysis changes. Please try again.');
          return;
        }
      }
      navigate();
    },
    [persistBeforeNavigate]
  );

  const onSelectStep = useCallback(
    (id) => {
      void withPersist(() => {
        if (id === 'parachute') {
          setParachuteSubTab(DEFAULT_PARACHUTE_WORKFLOW_SUB_ID);
          setParachuteEquitySubTab(DEFAULT_PARACHUTE_EQUITY_SUB_ID);
          setParachuteExpanded(true);
        }
        if (id === 'documents') {
          setDocumentsSubId(DEFAULT_DOCUMENTS_WORKFLOW_SUB_ID);
          setDocumentsExpanded(true);
        }
        setSelectedStepId(id);
      });
    },
    [
      setSelectedStepId,
      setParachuteExpanded,
      setParachuteSubId,
      setParachuteEquitySubId,
      setDocumentsExpanded,
      setDocumentsSubId,
      withPersist,
    ]
  );

  const onParachuteRowClick = useCallback(() => {
    if (isParachuteActive) {
      setParachuteExpanded((v) => !v);
    } else {
      void withPersist(() => {
        setParachuteSubId(DEFAULT_PARACHUTE_WORKFLOW_SUB_ID);
        setParachuteEquitySubId(DEFAULT_PARACHUTE_EQUITY_SUB_ID);
        setSelectedStepId('parachute');
        setParachuteExpanded(true);
      });
    }
  }, [
    isParachuteActive,
    setParachuteEquitySubId,
    setParachuteExpanded,
    setParachuteSubId,
    setSelectedStepId,
    withPersist,
  ]);

  const onParachuteSubClick = useCallback(
    (subId) => {
      void withPersist(() => {
        setSelectedStepId('parachute');
        setParachuteSubId(subId);
        if (subId !== 'equity') {
          setParachuteEquitySubId(DEFAULT_PARACHUTE_EQUITY_SUB_ID);
        }
        setParachuteExpanded(true);
      });
    },
    [setParachuteEquitySubId, setParachuteExpanded, setParachuteSubId, setSelectedStepId, withPersist]
  );

  const onParachuteEquityClick = useCallback(
    (equitySubId) => {
      void withPersist(() => {
        setSelectedStepId('parachute');
        setParachuteSubId('equity');
        setParachuteEquitySubId(equitySubId);
        setParachuteExpanded(true);
      });
    },
    [setParachuteEquitySubId, setParachuteExpanded, setParachuteSubId, setSelectedStepId, withPersist]
  );

  const onDocumentsRowClick = useCallback(() => {
    if (isDocumentsActive) {
      setDocumentsExpanded((v) => !v);
    } else {
      void withPersist(() => {
        setDocumentsSubId(DEFAULT_DOCUMENTS_WORKFLOW_SUB_ID);
        setSelectedStepId('documents');
        setDocumentsExpanded(true);
      });
    }
  }, [isDocumentsActive, setDocumentsExpanded, setDocumentsSubId, setSelectedStepId, withPersist]);

  const onDocumentsSubClick = useCallback(
    (subId) => {
      void withPersist(() => {
        setSelectedStepId('documents');
        setDocumentsSubId(subId);
        setDocumentsExpanded(true);
      });
    },
    [setDocumentsExpanded, setDocumentsSubId, setSelectedStepId, withPersist]
  );

  return (
    <div className={`flex-shrink-0 ${stepsCollapsed ? 'w-12' : 'w-full md:w-56 lg:w-72'}`}>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {stepsCollapsed ? (
          <div className="flex flex-col items-center border-b border-gray-200 py-3">
            <button
              type="button"
              onClick={() => setStepsCollapsed(false)}
              className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm transition-colors duration-150 hover:border-gray-500 hover:text-gray-700 hover:bg-gray-50"
              aria-label="Expand workflow sidebar"
            >
              <ChevronRight size={14} className="transform" aria-hidden />
            </button>
          </div>
        ) : (
          <div className="flex min-h-[2.75rem] items-center gap-2 border-b border-gray-200 py-2.5 px-3">
            <span className="min-w-0 flex-1 text-xs font-medium uppercase tracking-wider text-gray-500">Steps</span>
            <button
              type="button"
              onClick={() => setStepsCollapsed(true)}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm transition-colors duration-150 hover:border-gray-500 hover:text-gray-700 hover:bg-gray-50"
              aria-label="Collapse workflow sidebar"
            >
              <ChevronRight size={14} className="transform rotate-180" aria-hidden />
            </button>
          </div>
        )}
        <div className="p-3">
          {stepsCollapsed ? (
            <div className="flex flex-col items-center gap-1.5">
              <nav className="space-y-1.5" aria-label="Workflow steps">
                {steps.map((s) => {
                  const Icon = s.icon;
                  const active = selectedStepId === s.id;
                  const documentsSubLabel =
                    s.id === 'documents' && documentsSubId
                      ? DOCUMENTS_WORKFLOW_SUB_ITEMS.find((x) => x.id === documentsSubId)?.label
                      : null;
                  const collapsedAria =
                    s.id === 'documents' && documentsSubLabel
                      ? `${s.label}, ${documentsSubLabel}${s.done ? ', completed' : ''}`
                      : s.done
                        ? `${s.label}, completed`
                        : s.label;
                  const collapsedTitle =
                    s.id === 'documents' && documentsSubLabel ? `${s.label} — ${documentsSubLabel}` : s.label;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => onSelectStep(s.id)}
                      className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                        active
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                      }`}
                      aria-label={collapsedAria}
                      aria-current={active ? 'step' : undefined}
                      title={collapsedTitle}
                    >
                      <Icon size={14} aria-hidden />
                    </button>
                  );
                })}
              </nav>
            </div>
          ) : (
            <nav className="space-y-1" aria-label="Workflow steps">
              {steps.map((s) => {
                if (s.id === 'documents') {
                  const Icon = s.icon;
                  const subMenuId = 'workflow-documents-substeps';
                  return (
                    <div key={s.id}>
                      <button
                        type="button"
                        onClick={onDocumentsRowClick}
                        className={`flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left transition-all ${
                          isDocumentsActive ? 'bg-emerald-50 text-emerald-800' : 'text-gray-900 hover:bg-gray-100'
                        }`}
                        aria-expanded={documentsRowExpanded}
                        aria-controls={subMenuId}
                        aria-current={isDocumentsActive ? 'step' : undefined}
                      >
                        <div className="flex min-w-0 flex-1 items-center space-x-2.5">
                          <div
                            className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ${s.done ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-600'}`}
                          >
                            <Icon size={14} aria-hidden />
                          </div>
                          <span className={`min-w-0 truncate text-sm font-medium`}>{s.label}</span>
                        </div>
                        <ChevronRight
                          size={12}
                          className={`flex-shrink-0 transition-transform ${documentsRowExpanded ? 'rotate-90' : ''} ${
                            isDocumentsActive ? 'text-emerald-500' : 'text-gray-400'
                          }`}
                          aria-hidden
                        />
                      </button>
                      {documentsRowExpanded && (
                        <div
                          id={subMenuId}
                          className="ml-4 mt-1 space-y-1 border-l border-gray-200 pl-2.5"
                          role="group"
                          aria-label="Shareholder Approval sections"
                        >
                          {DOCUMENTS_WORKFLOW_SUB_ITEMS.map((sub) => {
                            const SubIcon = sub.icon;
                            const subActive = isDocumentsActive && documentsSubId === sub.id;
                            return (
                              <button
                                key={sub.id}
                                type="button"
                                onClick={() => onDocumentsSubClick(sub.id)}
                                className={`flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm transition-all ${
                                  subActive
                                    ? 'bg-emerald-50 font-medium text-emerald-800'
                                    : 'text-gray-600 hover:bg-gray-50'
                                }`}
                                aria-current={subActive ? 'true' : undefined}
                              >
                                <span
                                  className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded ${
                                    subActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                                  }`}
                                  aria-hidden
                                >
                                  <SubIcon size={12} strokeWidth={2} />
                                </span>
                                <span className="min-w-0 truncate">{sub.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                if (s.id !== 'parachute') {
                  const Icon = s.icon;
                  const active = selectedStepId === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => onSelectStep(s.id)}
                      className={`flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left transition-all ${
                        active ? 'bg-emerald-50 text-emerald-800' : 'text-gray-900 hover:bg-gray-100'
                      }`}
                      aria-current={active ? 'step' : undefined}
                    >
                      <div className="flex min-w-0 flex-1 items-center space-x-2.5">
                        <div
                          className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ${s.done ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-600'}`}
                        >
                          <Icon size={14} aria-hidden />
                        </div>
                        <span className="min-w-0 truncate text-sm font-medium">{s.label}</span>
                      </div>
                      <ChevronRight
                        size={12}
                        className={`flex-shrink-0 ${active ? 'text-emerald-500' : 'text-gray-400'}`}
                        aria-hidden
                      />
                    </button>
                  );
                }

                const Icon = s.icon;
                const subMenuId = 'workflow-parachute-substeps';
                return (
                  <div key={s.id}>
                    <button
                      type="button"
                      onClick={onParachuteRowClick}
                      className={`flex w-full items-center justify-between rounded-md px-3 py-2.5 text-left transition-all ${
                        isParachuteActive ? 'bg-emerald-50 text-emerald-800' : 'text-gray-900 hover:bg-gray-100'
                      }`}
                      aria-expanded={expanded}
                      aria-controls={subMenuId}
                      aria-current={isParachuteActive ? 'step' : undefined}
                    >
                      <div className="flex min-w-0 flex-1 items-center space-x-2.5">
                        <div
                          className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ${s.done ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-600'}`}
                        >
                          <Icon size={14} aria-hidden />
                        </div>
                        <span className="min-w-0 truncate text-sm font-medium">{s.label}</span>
                      </div>
                      <ChevronRight
                        size={12}
                        className={`flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''} ${
                          isParachuteActive ? 'text-emerald-500' : 'text-gray-400'
                        }`}
                        aria-hidden
                      />
                    </button>
                    {expanded && (
                      <div
                        id={subMenuId}
                        className="ml-4 mt-1 space-y-1 border-l border-gray-200 pl-2.5"
                        role="group"
                        aria-label="Parachute payment sections"
                      >
                        {PARACHUTE_WORKFLOW_SUB_ITEMS.map((sub) => {
                          const SubIcon = sub.icon;
                          const subDone = isParachuteSubItemComplete(formData, sub.id);
                          const iconBoxClass = subDone
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-600';

                          if (sub.id === 'equity') {
                            const awardsActive =
                              isParachuteActive && parachuteSubId === 'equity' && parachuteEquitySubId === 'awards';
                            const qaActive =
                              isParachuteActive && parachuteSubId === 'equity' && parachuteEquitySubId === 'qa24c';
                            return (
                              <div key={sub.id} className="space-y-0.5">
                                <button
                                  type="button"
                                  onClick={() => onParachuteEquityClick('awards')}
                                  className={`flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm transition-all ${
                                    awardsActive
                                      ? 'bg-emerald-50 font-medium text-emerald-800'
                                      : 'text-gray-600 hover:bg-gray-50'
                                  }`}
                                  aria-current={awardsActive ? 'true' : undefined}
                                >
                                  <span
                                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded ${iconBoxClass}`}
                                    aria-hidden
                                  >
                                    <SubIcon size={12} strokeWidth={2} />
                                  </span>
                                  <span className="min-w-0 truncate">{sub.label}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => onParachuteEquityClick('qa24c')}
                                  className={`ml-6 flex w-[calc(100%-1.5rem)] items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-all ${
                                    qaActive
                                      ? 'bg-emerald-50 font-medium text-emerald-800'
                                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'
                                  }`}
                                  aria-current={qaActive ? 'true' : undefined}
                                >
                                  <span
                                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded ${
                                      qaActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                                    }`}
                                    aria-hidden
                                  >
                                    <Calculator size={12} strokeWidth={2} />
                                  </span>
                                  <span className="min-w-0 truncate">{'Q&A-24(c)'}</span>
                                </button>
                              </div>
                            );
                          }

                          const subActive = isParachuteActive && parachuteSubId === sub.id;
                          return (
                            <button
                              key={sub.id}
                              type="button"
                              onClick={() => onParachuteSubClick(sub.id)}
                              className={`flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm transition-all ${
                                subActive
                                  ? 'bg-emerald-50 font-medium text-emerald-800'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                              aria-current={subActive ? 'true' : undefined}
                            >
                              <span
                                className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded ${iconBoxClass}`}
                                aria-hidden
                              >
                                <SubIcon size={12} strokeWidth={2} />
                              </span>
                              <span className="min-w-0 truncate">{sub.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          )}
        </div>
      </div>
    </div>
  );
}
